import { useCallback, useEffect, useState } from 'react';
import { Form, Select, DatePicker, Input, message } from 'antd';
import FormSection from 'components/ui/form-section';
import {
  CreateUploadedFilePayloadDtoEntityTypeEnum,
  FilterClaimsQueryDtoStatusesEnum,
  ClaimCheckResponseDtoResultEnum,
  CreateClaimPayloadDto,
  ClaimResponseDto,
} from 'libs/pinnacle-sdk';
import { ConfirmationModal } from 'components/ui/confirmation-modal';
import { isEqual } from 'lodash';
import { sanitizeFormValue, validateForm, handleError } from 'utils/form';
import { ConfirmationModalType } from 'components/ui/confirmation-modal/types';
import { ArrowDownIcon } from 'assets/icons/arrow-down';
import CalendarIcon from 'assets/icons/calendar';
import CopyIcon from 'assets/icons/copy';
import {
  getValidationSchema,
  initialValues,
  fields,
  CLAIM_AUTO_NAVIGATE_DELAY,
  ContractDateRange,
} from './form';
import { useNavigate } from 'react-router';
import { constructClaimDetailPath, ENavigationPath } from 'constants/path';
import { EDashboardTab } from 'components/features/dashboard-tabs/types';
import FormContainer from 'components/ui/form-container';
import {
  insuredApi,
  referenceDataApi,
  userApi,
  claimsApi,
  catEventApi,
  submissionApi,
  contractApi,
} from 'services/apis';
import FormLayoutGroup from 'components/ui/form-layout-group';
import { useTranslation } from 'react-i18next';
import InfoModal from 'components/ui/info-modal';
import ValidationItem from 'components/ui/validation-item';
import { LazyLoadingSelect } from 'components/ui/lazy-loading-select';
import CustomBadge from 'components/ui/custom-badge';
import { useMutation, useQuery } from 'react-query';
import { EQueryKeys } from 'constants/query-keys';
import TextArea from 'antd/es/input/TextArea';
import FileUploader from 'components/features/file-uploader';
import { useFileSession } from 'hooks/useFileSession';
import { countryStateCityApi } from 'services/apis';
import { uniqBy, debounce } from 'lodash';
import FormActionButtons from 'components/ui/form-action-buttons';
import { EFileStatus } from '../file-uploader/enum';
import { SortedSelect } from 'components/ui/sorted-select';
import { StatusCheck } from '../submission-review/types';
import { getLocalDateForMidnightUTC } from 'utils';

interface RegisterMasterClaimFormProps {
  mode?: 'create' | 'update';
  initialData?: object;
  onSubmit?: (data: CreateClaimPayloadDto) => Promise<void>;
  isLoading?: boolean;
}

const RegisterMasterClaimForm = ({
  mode = 'create',
  initialData = initialValues,
  onSubmit,
  isLoading = false,
}: RegisterMasterClaimFormProps) => {
  const [isFormValid, setIsFormValid] = useState(false);
  const [form] = Form.useForm<CreateClaimPayloadDto>();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { fileSessionId, fileList, setFileList } = useFileSession();
  const lineOfBusiness = Form.useWatch('lineOfBusiness', form);
  const insuredId = Form.useWatch('insuredId', form);
  const [countryIso, setCountryIso] = useState<string | undefined>(undefined);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationStatus, setValidationStatus] = useState<StatusCheck>({
    duplicateClaimCheck: 'Pending',
  });
  const [validationKey, setValidationKey] =
    useState<string>('duplicate-check-0');
  const [autoNavigateCountdown, setAutoNavigateCountdown] = useState<
    number | null
  >(null);

  const { data: insuredLineOfBusinesses, isLoading: isLoadingInsuredLOB } =
    useQuery(
      [EQueryKeys.INSURED_BY_ID, insuredId],
      async () => {
        if (!insuredId) return [];
        const { items } = await submissionApi.filterSubmissions({
          filterSubmissionsQueryDto: {
            insureds: [insuredId],
          },
        });

        const uniqueLOBs = Array.from(
          new Set(items.map((submission) => submission.lineOfBusiness))
        ).filter(Boolean);
        return uniqueLOBs;
      },
      {
        refetchOnWindowFocus: false,
        enabled: !!insuredId,
      }
    );

  const { data: currencies, isLoading: isLoadingCurrencies } = useQuery(
    EQueryKeys.CURRENCIES,
    async () => {
      return referenceDataApi.getCurrencies();
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  const { data: subClasses, isLoading: isLoadingSubClasses } = useQuery(
    [EQueryKeys.SUB_CLASSES, lineOfBusiness],
    async () => {
      return referenceDataApi.getSubClasses({ lineOfBusiness });
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  const { data: lossCauses, isLoading: isLoadingLossCauses } = useQuery(
    [EQueryKeys.LOSS_CAUSES],
    async () => {
      return referenceDataApi.getLossCauses();
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  const { data: countries, isLoading: isLoadingCountries } = useQuery(
    EQueryKeys.COUNTRIES,
    async () => {
      return countryStateCityApi.getCountries();
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  const { data: cities, isLoading: isLoadingCities } = useQuery(
    [EQueryKeys.CITIES, countryIso],
    async () => {
      if (!countryIso) return;
      return countryStateCityApi.getCitiesByStateAndCountry({
        countryIso,
      });
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  const { data: contracts } = useQuery(
    [EQueryKeys.CONTRACT_LIST, insuredId, lineOfBusiness],
    async () => {
      if (!insuredId || !lineOfBusiness) return undefined;
      return contractApi.filterContracts({
        contractFilterQueryDto: {
          insuredIds: [insuredId],
          lineOfBusinesses: [lineOfBusiness],
          includeSubmission: true,
        },
      });
    },
    {
      enabled: !!insuredId && !!lineOfBusiness,
      refetchOnWindowFocus: false,
    }
  );

  const contractDateRanges: ContractDateRange[] | undefined = contracts?.items
    .filter((contract) => contract.submission)
    .map((contract) => ({
      inceptionDate: getLocalDateForMidnightUTC(
        contract.submission!.inceptionDate
      ).toDate(),
      expiryDate: getLocalDateForMidnightUTC(
        contract.submission?.expiryDate ?? new Date()
      ).toDate(),
    }));

  const handleValidationCancel = useCallback(() => {
    setShowValidationModal(false);
    setAutoNavigateCountdown(null);
  }, []);

  useEffect(() => {
    if (countries && initialValues?.country) {
      const selectedCountry = countries.find(
        (country) => country.name === initialValues.country
      );
      if (selectedCountry) {
        setCountryIso(selectedCountry.iso);
      }
    }
  }, [countries, initialValues?.country]);

  useEffect(() => {
    if (!insuredLineOfBusinesses || insuredLineOfBusinesses.length === 0) {
      return;
    }

    if (insuredLineOfBusinesses.length === 1) {
      form.setFieldValue('lineOfBusiness', insuredLineOfBusinesses[0]);
    } else {
      form.setFieldValue('lineOfBusiness', undefined);
    }

    form.setFieldValue('subClass', undefined);
  }, [insuredLineOfBusinesses, form]);

  useEffect(() => {
    if (!showValidationModal) return;

    const allChecksPassed =
      validationStatus.duplicateClaimCheck ===
      ClaimCheckResponseDtoResultEnum.Passed;

    if (allChecksPassed && autoNavigateCountdown === null) {
      setAutoNavigateCountdown(CLAIM_AUTO_NAVIGATE_DELAY);
    }
  }, [validationStatus, showValidationModal, autoNavigateCountdown]);

  const handleClaimCheckResult = useCallback(() => {
    handleValidationCancel();

    const sanitizedMasterClaim = sanitizeFormValue(form.getFieldsValue());
    debouncedUpsertMasterClaimMutate(sanitizedMasterClaim, (data) =>
      navigate(constructClaimDetailPath(data.claimId))
    );
  }, [validationStatus.duplicateClaimCheck, navigate]);

  const getContinueButtonText = useCallback(() => {
    if (autoNavigateCountdown !== null && autoNavigateCountdown > 0) {
      return `${t('claim.continue')} (${autoNavigateCountdown})`;
    }

    return t('claim.continue');
  }, [autoNavigateCountdown, t]);

  const getDuplicateCheckStatus = useCallback(async () => {
    const sanitizedMasterClaim = sanitizeFormValue(form.getFieldsValue());

    return await claimsApi.checkDuplicatedClaim({
      claimDuplicationCheckPayloadDto: {
        insuredId: sanitizedMasterClaim.insuredId,
        lineOfBusiness: sanitizedMasterClaim.lineOfBusiness,
        subClass: sanitizedMasterClaim.subClass,
        dateOfLoss: sanitizedMasterClaim.dateOfLoss,
      },
    });
  }, [form, validationKey]);

  const handleOnFinish = useCallback(async () => {
    setShowValidationModal(true);
    setValidationStatus({ duplicateClaimCheck: 'Pending' });
    setValidationKey(`duplicate-check-${Date.now()}`);
  }, []);

  useEffect(() => {
    if (autoNavigateCountdown !== null && autoNavigateCountdown > 0) {
      const timer = setTimeout(() => {
        setAutoNavigateCountdown(autoNavigateCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }

    if (autoNavigateCountdown === 0) {
      handleClaimCheckResult();
    }
  }, [autoNavigateCountdown, navigate, handleClaimCheckResult]);

  const resetAndNavigate = useCallback(() => {
    form.resetFields();
    setShowConfirmation(false);
    navigate(ENavigationPath.DASHBOARD, {
      state: { activeTab: EDashboardTab.CLAIM },
    });
  }, [form, navigate]);

  const cancelClaim = useCallback(() => {
    const currentValue = form.getFieldsValue();
    if (isEqual(currentValue, initialValues)) return resetAndNavigate();

    return setShowConfirmation(true);
  }, [form, resetAndNavigate]);

  const createClaimMutation = useMutation(
    (masterClaim: CreateClaimPayloadDto) =>
      claimsApi.createClaim({
        createClaimPayloadDto: masterClaim,
      }),
    {
      onSuccess: () => {
        message.success(
          t('forms.createRecordSuccess', { type: t('claim.masterClaim') })
        );
      },
      onError: async (error: { response: Response }) => {
        handleError({
          error,
          defaultMessage: t('forms.createRecordFail', {
            type: t('claim.masterClaim'),
          }),
        });
      },
    }
  );

  const debouncedUpsertMasterClaimMutate = useCallback(
    debounce(
      async (
        masterClaim: CreateClaimPayloadDto,
        onSuccess?: (data: ClaimResponseDto) => void,
        onError?: () => void
      ) => {
        if (mode === 'update' && onSubmit) {
          onSubmit(masterClaim)
            .then(() => {
              message.success(
                t('forms.updateRecordSuccess', { type: t('claim.masterClaim') })
              );
              navigate(ENavigationPath.DASHBOARD, {
                state: { activeTab: EDashboardTab.CLAIM },
              });
            })
            .catch((error) => {
              handleError({
                error,
                defaultMessage: t('forms.updateRecordFail', {
                  type: t('claim.masterClaim'),
                }),
              });
            });
        } else {
          const fileIds = fileList
            .filter((file) => file.id)
            .map((file) => file.id) as string[];

          const masterClaimPayload: CreateClaimPayloadDto = {
            ...masterClaim,
            fileIds: fileIds.length > 0 ? fileIds : undefined,
          };

          createClaimMutation.mutate(masterClaimPayload, {
            onSuccess,
            onError,
          });
        }
      },
      500
    ),
    [mode, onSubmit, navigate, t, createClaimMutation, fileList]
  );

  /**
   * This useEffect initializes the form with the provided initialData when component mounts or initialData changes.
   * Dependencies:
   * - form: The form instance to update
   * - initialData: The data to initialize the form with
   * - t: Translation function for validation messages
   */
  useEffect(() => {
    if (initialData) {
      form.setFieldsValue(initialData);
      validateForm<CreateClaimPayloadDto>(
        initialData as CreateClaimPayloadDto,
        form,
        fields,
        getValidationSchema(t, contractDateRanges)
      ).then(setIsFormValid);
    }
  }, [form, initialData, t]);

  /**
   * This useEffect re-validates the form whenever contractDateRanges changes.
   * Dependencies:
   * - form: To get current form values
   * - t: Translation function for validation messages
   * - contractDateRanges: Date ranges that affect validation rules
   */
  useEffect(() => {
    const values = form.getFieldsValue();
    validateForm<CreateClaimPayloadDto>(
      values as CreateClaimPayloadDto,
      form,
      fields,
      getValidationSchema(t, contractDateRanges)
    ).then(setIsFormValid);
  }, [form, t, contractDateRanges]);

  return (
    <FormContainer
      title={
        mode === 'create'
          ? t('claim.setupMasterClaim')
          : t('claim.updateMasterClaim')
      }
      loading={isLoading}
    >
      <Form
        form={form}
        layout="vertical"
        className="space-y-8"
        initialValues={initialValues}
        onValuesChange={async (changedValues) => {
          if ('lineOfBusiness' in changedValues) {
            form.setFieldValue('subClass', undefined);
          }

          if ('country' in changedValues) {
            form.setFieldValue('city', undefined);
          }

          const values = form.getFieldsValue();
          const isValid = await validateForm(
            values,
            form,
            fields as (keyof CreateClaimPayloadDto)[],
            getValidationSchema(t, contractDateRanges)
          );
          setIsFormValid(isValid);
        }}
        onFinish={handleOnFinish}
      >
        <FormSection
          title={t('claim.transactionDetailsAndClassification')}
          collapsible
        >
          <FormLayoutGroup mdColumns={2}>
            <Form.Item name="insuredId" label={t('insured.insured')}>
              <LazyLoadingSelect
                fetchOptions={async (page, pageSize, searchText) => {
                  const { items } = await insuredApi.filterInsuredsClaims({
                    page,
                    pageSize,
                    insuredFilterQueryDto: {
                      searchText,
                    },
                  });
                  return {
                    data: items.map((insured) => ({
                      label: insured.name,
                      value: insured.insuredId,
                    })),
                  };
                }}
                suffixIcon={<ArrowDownIcon />}
                allowClear
                size="large"
                placeholder={t('claim.selectInsured')}
                queryKey="insured-lazy-loading-select"
              />
            </Form.Item>

            <Form.Item name="status" label={t('claim.status')}>
              <SortedSelect
                allowClear
                size="large"
                placeholder={t('claim.selectMasterClaimsStatus')}
                options={Object.values(FilterClaimsQueryDtoStatusesEnum).map(
                  (status) => ({
                    label: status,
                    value: status,
                  })
                )}
                optionRender={(option) => (
                  <CustomBadge
                    label={option.value as FilterClaimsQueryDtoStatusesEnum}
                    backgroundColor="bg-[#DBF6FE]"
                    textColor="text-[#3480D1]"
                  />
                )}
                labelRender={(props) => (
                  <CustomBadge
                    label={
                      /* eslint-disable-next-line react/prop-types */
                      props.value as FilterClaimsQueryDtoStatusesEnum
                    }
                    backgroundColor="bg-[#DBF6FE]"
                    textColor="text-[#3480D1]"
                  />
                )}
                suffixIcon={<ArrowDownIcon />}
              ></SortedSelect>
            </Form.Item>
          </FormLayoutGroup>

          <FormLayoutGroup mdColumns={2}>
            <Form.Item name="lineOfBusiness" label={t('claim.lineOfBusiness')}>
              {insuredLineOfBusinesses &&
              insuredLineOfBusinesses.length === 1 ? (
                <Input
                  size="large"
                  value={insuredLineOfBusinesses[0]}
                  disabled
                />
              ) : (
                <SortedSelect
                  suffixIcon={<ArrowDownIcon />}
                  allowClear
                  size="large"
                  loading={isLoadingInsuredLOB}
                  placeholder={t('claim.selectLineOfBusiness')}
                  disabled={!insuredId || !insuredLineOfBusinesses}
                  options={(insuredLineOfBusinesses || []).map((lob) => ({
                    label: lob,
                    value: lob,
                  }))}
                />
              )}
            </Form.Item>

            <Form.Item name="subClass" label={t('claim.subClass')}>
              <SortedSelect
                suffixIcon={<ArrowDownIcon />}
                allowClear
                size="large"
                disabled={!lineOfBusiness}
                loading={isLoadingSubClasses}
                placeholder={t('claim.selectSubClass')}
                options={(subClasses || []).map((subClass) => ({
                  label: subClass.name,
                  value: subClass.name,
                }))}
              ></SortedSelect>
            </Form.Item>
          </FormLayoutGroup>
        </FormSection>

        <FormSection title={t('claim.setupDetails')} collapsible>
          <FormLayoutGroup mdColumns={2}>
            <Form.Item name="claimsHandler" label={t('claim.claimsHandler')}>
              <LazyLoadingSelect
                fetchOptions={async (page, pageSize, searchText) => {
                  const { items } = await userApi.filterUsers({
                    searchText,
                    page,
                    pageSize,
                  });
                  return {
                    data: items.map((user) => ({
                      label: user.name || '',
                      value: user.userId,
                    })),
                  };
                }}
                suffixIcon={<ArrowDownIcon />}
                allowClear
                size="large"
                placeholder={t('claim.selectClaimsHandler')}
                queryKey="claims-handler-lazy-loading-select"
              />
            </Form.Item>

            <Form.Item name="dateReported" label={t('claim.dateReported')}>
              <DatePicker
                format={'DD/MM/YYYY'}
                allowClear
                size="large"
                className="w-full"
                suffixIcon={<CalendarIcon />}
                placeholder={t('claim.selectDate')}
              />
            </Form.Item>
          </FormLayoutGroup>

          <FormLayoutGroup mdColumns={2}>
            <Form.Item name="dateOfLoss" label={t('claim.dateOfLoss')}>
              <DatePicker
                format={'DD/MM/YYYY'}
                allowClear
                size="large"
                className="w-full"
                suffixIcon={<CalendarIcon />}
                placeholder={t('claim.selectDate')}
              />
            </Form.Item>

            <Form.Item name="catCode" label={t('claim.selectCatCode')}>
              <LazyLoadingSelect
                fetchOptions={async (page, pageSize, searchText) => {
                  const res = await catEventApi.filterCatEvents({
                    searchText,
                    page,
                    pageSize,
                  });
                  return {
                    data: (res.items || []).map((cat) => ({
                      label: cat.eventName,
                      value: cat.catEventId,
                    })),
                  };
                }}
                suffixIcon={<ArrowDownIcon />}
                allowClear
                size="large"
                placeholder={t('claim.selectCatCode')}
                queryKey="cat-code-lazy-loading-select"
              />
            </Form.Item>
          </FormLayoutGroup>

          <FormLayoutGroup mdColumns={2}>
            <Form.Item name="causeOfLoss" label={t('claim.causeOfLoss')}>
              <SortedSelect
                suffixIcon={<ArrowDownIcon />}
                allowClear
                size="large"
                placeholder={t('claim.selectCauseOfLoss')}
                showSearch
                loading={isLoadingLossCauses}
                options={(lossCauses || []).map((lossCause) => ({
                  label: lossCause.lossCauseName,
                  value: lossCause.lossCauseName,
                }))}
              />
            </Form.Item>

            <Form.Item name="currency" label={t('claim.currency')}>
              <SortedSelect
                suffixIcon={<ArrowDownIcon />}
                allowClear
                size="large"
                showSearch
                placeholder={t('claim.selectContractCurrency')}
                loading={isLoadingCurrencies}
                options={(currencies || []).map((currency) => ({
                  label: `${currency.name} (${currency.code})`,
                  value: currency.code,
                }))}
              />
            </Form.Item>
          </FormLayoutGroup>

          <FormLayoutGroup mdColumns={3}>
            <Form.Item name="country" label={t('general.country')}>
              <SortedSelect
                allowClear
                showSearch
                size="large"
                loading={isLoadingCountries}
                placeholder={t('general.selectField', {
                  field: t('general.country'),
                })}
                suffixIcon={<ArrowDownIcon />}
                className="w-full"
                onChange={(value) => {
                  const selectedCountry = countries?.find(
                    (country) => country.name === value
                  );
                  setCountryIso(selectedCountry?.iso);
                }}
                options={uniqBy(countries || [], (country) => country.name).map(
                  (country) => ({
                    value: country.name,
                    label: `${country.emoji} ${country.name}`,
                    key: country.id,
                  })
                )}
                optionFilterProp="value"
              ></SortedSelect>
            </Form.Item>

            <Form.Item name="city" label={t('general.city')}>
              <SortedSelect
                allowClear
                showSearch
                size="large"
                placeholder={t('general.selectField', {
                  field: t('general.city'),
                })}
                loading={isLoadingCities}
                disabled={!countryIso}
                suffixIcon={<ArrowDownIcon />}
                className="w-full"
                options={uniqBy(cities || [], (city) => city.name).map(
                  (city) => ({
                    value: city.name,
                    label: city.name,
                    key: city.id,
                  })
                )}
                optionFilterProp="value"
              ></SortedSelect>
            </Form.Item>

            <Form.Item name="lossLocation" label={t('claim.lossLocation')}>
              <Input size="large" placeholder={t('claim.enterLossLocation')} />
            </Form.Item>
          </FormLayoutGroup>

          <FormLayoutGroup mdColumns={1}>
            <Form.Item
              name="lossDescription"
              label={t('claim.lossDescription')}
            >
              <TextArea rows={4} placeholder={t('claim.enterCommentHere')} />
            </Form.Item>
          </FormLayoutGroup>
        </FormSection>

        <FormSection title={t('claim.uploadFile')} collapsible>
          <FormLayoutGroup mdColumns={1}>
            <Form.Item className="mt-6">
              {fileSessionId && (
                <FileUploader
                  sessionId={fileSessionId}
                  entityType={CreateUploadedFilePayloadDtoEntityTypeEnum.Claim}
                  fileList={fileList}
                  setFileList={setFileList}
                />
              )}
            </Form.Item>
          </FormLayoutGroup>
        </FormSection>

        <FormActionButtons
          onCancel={cancelClaim}
          isConfirmDisabled={
            !isFormValid ||
            isLoading ||
            createClaimMutation.isLoading ||
            fileList.some((file) => file.status === EFileStatus.UPLOADING)
          }
          confirmText={t(
            mode === 'create' ? 'claim.createClaim' : 'general.save'
          )}
        />
      </Form>
      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={resetAndNavigate}
        title={t('forms.discardChangesTitle')}
        content={t('forms.discardChangesContent', {
          type: t('claim.setupMasterClaimLowercase'),
        })}
        okText={t('forms.discardChangesOkText')}
        cancelText={t('forms.discardChangesCancelText')}
        type={ConfirmationModalType.DANGER}
      />
      <InfoModal
        isOpen={showValidationModal}
        onClose={handleValidationCancel}
        onConfirm={handleClaimCheckResult}
        title={t('claim.validationCheck')}
        okText={getContinueButtonText()}
        subTitle={t('claim.ourSystemIsAutomaticallyVerifyingYourInformation')}
        size="medium"
        isDisabled={
          validationStatus.duplicateClaimCheck ===
          ClaimCheckResponseDtoResultEnum.Partial
        }
      >
        <ValidationItem
          key={validationKey}
          title={t('claim.duplicateCheck')}
          icon={<CopyIcon />}
          getValidationStatus={getDuplicateCheckStatus}
          setStatusCheck={setValidationStatus}
          statusKey="duplicateClaimCheck"
        />
      </InfoModal>
    </FormContainer>
  );
};

export default RegisterMasterClaimForm;
